import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import axios from "axios";
import config from "../../config";
import Swal from "sweetalert2";

import { IoMdDownload } from "react-icons/io";
import { FaEye } from "react-icons/fa";
import { HiOutlineCollection } from "react-icons/hi";

export default function TabHK({ measureTitle }) {
  const { id } = useParams();
  const { register, handleSubmit, watch, setValue } = useForm();
  const [measureTiles, setMeasureTiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [checkedItems, setCheckedItems] = useState({});
  const [measureData, setMeasureData] = useState({});
  const [getMeasureResult, setGetMeasureResult] = useState({});
  const [formulaMap, setFormulaMap] = useState({});
  const [measureFormulas, setMeasureFormulas] = useState({});

  const activityList = [
    {
      key: 'announce',
      label: 'ติดประกาศ/รณรงค์ประชาสัมพันธ์'
    },
    {
      key: 'owner',
      label: 'คณะทำงาน/ผู้รับผิดชอบ'
    },
    {
      key: 'auto',
      label: 'Automation/การควบคุมอัตโนมัติ'
    },
  ];


  // Fetch data from the API
  const fetchDataApi = useCallback(async () => {
    try {
      const res = await axios.get(config.urlApi + "/measure");
      const fetchedMeasures = res.data;

      // Filter measure tiles based on measureTitle
      const filteredTiles = fetchedMeasures
        .flatMap(measure => measure.categorymeasures)
        .filter(category => category.id === parseInt(measureTitle))
        .flatMap(category => category.measure_tiles);

      setMeasureTiles(filteredTiles);
    } catch (err) {
      console.error(err.message);
    }
  }, [measureTitle]);

  const fetchMeasureFormula = useCallback(async (measureListId) => {
    if (measureFormulas[measureListId]) return;

    try {
      const res = await axios.get(
        `${config.urlApi}/measures/measure-list-formula/${measureListId}`
      );

      if (res.status === 200 && res.data?.formula_json) {
        setMeasureFormulas(prev => ({
          ...prev,
          [measureListId]: res.data.formula_json
        }));
      }
    } catch {
      // ไม่มีสูตร = ใช้ค่า 0
      setMeasureFormulas(prev => ({
        ...prev,
        [measureListId]: {}
      }));
    }
  }, [measureFormulas]);

  const fetchMeasuresResult = useCallback(async () => {
    const res = await axios.get(
      `${config.urlApi}/getMeasureResult?activityperiod_id=${id}`
    );

    if (res.status !== 200) return;

    const data = res.data?.data;
    setGetMeasureResult(data);

    if (!data?.measure) return;

    Object.entries(data.measure).forEach(([key, m]) => {
      const measureId = key.replace('list_id', '');

      /* เปิด measure ที่เคยบันทึก */
      setCheckedItems(prev => ({ ...prev, [measureId]: true }));

      /* ✅ ตั้งค่า checkbox จาก API */
      setValue(`activity_measure${measureId}`,
        Array.isArray(m.activity_measure)
          ? m.activity_measure.map(String)
          : []
      );

      setValue(`report_measure${measureId}`,
        Array.isArray(m.report_measure)
          ? m.report_measure.map(String)
          : []
      );

      setMeasureData(prev => ({
        ...prev,
        [measureId]: {
          worksite_area: Array.isArray(m.worksite_area)
            ? m.worksite_area
            : m.worksite_area ? [m.worksite_area] : [],
          amount_item: Array.isArray(m.amount_item)
            ? m.amount_item
            : m.amount_item ? [m.amount_item] : [],
          size: Array.isArray(m.size)
            ? m.size
            : m.size ? [m.size] : [],
          unit: Array.isArray(m.unit)
            ? m.unit
            : m.unit ? [m.unit] : [],
        }
      }));
    });
  }, [id, setValue]);

  // Handle next page
  const handleNextPage = () => {
    if (currentPage < measureTiles.length - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      Swal.fire("No more pages", "You have reached the last page", "info");
    }
  };

  // Handle previous page
  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const totalSteps = measureTiles.length;
  const currentStep = currentPage + 1;
  const progressValue = totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0;

  const handleCheckboxChange = (id) => {
    setCheckedItems(prev => {
      const newChecked = !prev[id];

      if (!newChecked) {
        setValue(`activity_measure${id}`, []);
        setValue(`report_measure${id}`, []);
      }

      return {
        ...prev,
        [id]: newChecked,
      };
    });
  };

  const evaluateFormula = (tokens, valueMap, totalsize) => {
    if (!tokens?.length) return 0;

    const expr = tokens
      .map(t => {
        if (t.type === "input" && t.key === "TOTAL_SIZE") return Number(totalsize || 0)
        if (t.type === "operator") return t.value;
        if (t.type === "number") return t.value;
        if (t.type === "measure") {
          return Number(valueMap[t.id] ?? 0);
        }
        return "";
      })
      .join("");

    if (!expr || expr.trim() === "") return 0;

    try {
      // eslint-disable-next-line no-eval
      const result = eval(expr);
      return Number.isFinite(result) ? result : 0;
    } catch (err) {
      return 0;
    }
  };

  const fetchFormulasByCategory = useCallback(async (cate_id) => {
    if (formulaMap[cate_id]) return;

    try {
      const res = await axios.get(
        `${config.urlApi}/measures/formulas?category_id=${cate_id}`
      );

      if (res.status === 200) {
        const ef = res.data.find(f => f.name === 'EF ไฟฟ้า')?.value || 0;

        setFormulaMap(prev => ({
          ...prev,
          [cate_id]: {
            formulas: res.data,
            ef: parseFloat(ef)
          }
        }));
      }
    } catch (err) {
      console.error(err);
    }
  }, [formulaMap]);

  const calcSavings = async (totalsize, cate_id, activity, measureListId) => {
    const formulaSet = measureFormulas[measureListId];
    if (!formulaSet) return 0;

    let parsed;
    try {
      parsed = JSON.parse(formulaSet);
    } catch {
      return 0;
    }


    const tokens = parsed?.[activity];
    if (!tokens?.length) return 0;

    const baseFormulas = formulaMap[cate_id]?.formulas || [];

    const valueMap = {};
    baseFormulas.forEach(f => {
      valueMap[f.id] = Number(f.value ?? 0);
    });

    const result = evaluateFormula(tokens, valueMap, totalsize);
    return result.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  const handleArrayFieldChange = async (measureId, fieldName, index, value, cate_id, activity, measureName) => {
    let newValue = value;

    if (fieldName === 'size') {
      newValue = await calcSavings(value, cate_id, activity, measureId);
    }

    setMeasureData(prev => {
      const newData = { ...prev };
      if (!newData[measureId]) newData[measureId] = {};
      if (!Array.isArray(newData[measureId][fieldName])) {
        newData[measureId][fieldName] = [];
      }

      newData[measureId][fieldName][index] = value;
      if (fieldName === 'size') {
        if (!Array.isArray(newData[measureId]['unit'])) {
          newData[measureId]['unit'] = [];
        }
        newData[measureId]['unit'][index] = newValue;
      }

      return newData;
    });
  };

  // Helper function to render
  const renderActivityFields = (measureId, activityIndex, cate_id, activity, measureName) => {
    return (
      <div className="mt-2 ps-3 border-start">
        <div className="row g-2">
          <div className="col-md-6">
            <label className="small text-muted">จุดที่ดำเนินการ</label>
            <input
              type="text"
              className="form-control form-control-sm"
              onChange={(e) => handleArrayFieldChange(
                measureId,
                'worksite_area',
                activityIndex,
                e.target.value
              )}
              value={measureData[measureId]?.worksite_area?.[activityIndex] ||
                getMeasureResult?.measure?.[`list_id${measureId}`]?.worksite_area?.[activityIndex] || ''}
            />
          </div>
          <div className="col-md-6">
            <label className="small text-muted">จำนวนอุปกรณ์</label>
            <input
              type="number"
              className="form-control form-control-sm"
              onChange={(e) => handleArrayFieldChange(
                measureId,
                'amount_item',
                activityIndex,
                e.target.value
              )}
              value={measureData[measureId]?.amount_item?.[activityIndex] ||
                getMeasureResult?.measure?.[`list_id${measureId}`]?.amount_item?.[activityIndex] || ''}
            />
          </div>
          <div className="col-md-6">
            <label className="small text-muted">ขนาด(รวม)</label>
            <input
              type="text"
              className="form-control form-control-sm"
              onChange={(e) => handleArrayFieldChange(
                measureId,
                'size',
                activityIndex,
                e.target.value,
                cate_id,
                activity,
                measureName
              )}
              value={measureData[measureId]?.size?.[activityIndex] ||
                getMeasureResult?.measure?.[`list_id${measureId}`]?.size?.[activityIndex] || ''}
            />
          </div>
          <div className="col-md-6">
            <label className="small text-muted">ผลประหยัด (หน่วย/ปี)</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={measureData[measureId]?.unit?.[activityIndex] ||
                getMeasureResult?.measure?.[`list_id${measureId}`]?.unit?.[activityIndex] || ''}
              readOnly
            />
          </div>
        </div>

      </div>
    );
  }

  const renderCheckboxGroup = (name, options, id) => (
    <div className="d-flex flex-column gap-2">
      {options.map((label, idx) => (
        <div key={idx} className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            name={`${name}${id}`}
            value={idx + 1}
            {...register(`${name}${id}`)}
          />
          <label className="form-check-label">{label}</label>
        </div>
      ))}
    </div>
  );

  const renderFileLink = (fieldName, measureId) => {
    const measureKey = `list_id${measureId}`;
    const fileName = getMeasureResult?.measure?.[measureKey]?.[fieldName];

    if (!fileName) return null;

    return (
      <div className="mb-3">
        <div
          className="d-flex align-items-center p-2 rounded"
          style={{
            background: "rgba(25,135,84,.08)"
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center rounded-circle me-2"
            style={{
              width: 32,
              height: 32,
              background: "rgba(25,135,84,.15)"
            }}
          >
            <FaEye size={16} className="text-success" />
          </div>
          <div className="flex-grow-1">
            <a
              href={`${config.urlApi}/ref_doc/${fileName}`}
              target="_blank"
              rel="noopener noreferrer"
              className="fw-medium text-success text-decoration-none"
            >
              ดูไฟล์ที่อัปโหลดแล้ว:
              <div className="small text-muted text-truncate">
                {fileName}
              </div>
            </a>
          </div>

        </div>

      </div>
    );
  };

  const onSubmit = async (data) => {
    const formData = new FormData();
    const payload = {
      activityperiod_id: id,
      measures: {}
    };

    // Get all unique measure IDs from the data
    const measureIds = Object.keys(data)
      .filter(key => key.startsWith('list_id'))
      .map(key => key.replace('list_id', ''));

    // Check if at least one measure is selected
    if (measureIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Warning',
        text: 'กรุณาเลือกอย่างน้อยหนึ่งรายการก่อนบันทึก!',
      });
      return;
    }

    // Process each measure
    measureIds.forEach(measureId => {
      payload.measures[`list_id${measureId}`] = {
        activity_measure: data[`activity_measure${measureId}`] || [],
        activity_ref: null, // ตั้งค่าเริ่มต้นเป็น null (จะมาแก้ไขหลังอัปโหลดไฟล์)
        amount_item: data[`amount_item${measureId}`] || [],
        report_measure: data[`report_measure${measureId}`] || [],
        size: data[`size${measureId}`] || [],
        unit: data[`unit${measureId}`] || [],
        worksite_area: data[`worksite_area${measureId}`] || [],
        result_saving: data[`result_saving${measureId}`] || null,
        saving_unit: data[`saving_unit${measureId}`] || null,
        saving_ref: null,
        report_ref: null
      };

      // Add files to formData if they exist
      if (data[`activity_ref${measureId}`]?.[0]) {
        formData.append(`activity_ref_${measureId}`, data[`activity_ref${measureId}`][0]);
      }
      if (data[`report_ref${measureId}`]?.[0]) {
        formData.append(`report_ref_${measureId}`, data[`report_ref${measureId}`][0]);
      }
      if (data[`saving_ref${measureId}`]?.[0]) {
        formData.append(`saving_ref_${measureId}`, data[`saving_ref${measureId}`][0]);
      }
    });

    formData.append('payload', JSON.stringify(payload));


    try {
      const res = await axios.post(`${config.urlApi}/savemeasure`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: res?.data?.massage || 'บันทึกข้อมูลสำเร็จ!',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล'
      });
    }
  };

  useEffect(() => {
    fetchDataApi();
    fetchMeasuresResult();
  }, [fetchDataApi, fetchMeasuresResult]);

  useEffect(() => {
    Object.entries(measureData).forEach(([measureId, fields]) => {
      Object.entries(fields).forEach(([fieldName, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val, index) => {
            setValue(`${fieldName}${measureId}.${index}`, val);
          });
        } else {
          setValue(`${fieldName}${measureId}`, value);
        }
      });
    });
  }, [measureData, setValue]);

  useEffect(() => {
    Object.entries(measureData).forEach(([measureId, fields]) => {
      const totalNumber = fields.unit
        ?.map(v => parseFloat(v) || 0)
        .reduce((a, b) => a + b, 0) || 0;

      const totalDisplay = totalNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      const cateId = measureTiles.find(tile =>
        tile.measure_lists?.some(m => m.id === parseInt(measureId))
      )?.cate_id;

      const ef = Number(formulaMap[cateId]?.ef) || 0;
      const tco2eNumber = (totalNumber * ef) / 1000;
      const tco2eDisplay = tco2eNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      setValue(`result_saving${measureId}`, totalDisplay);
      setValue(`saving_unit${measureId}`, 'หน่วย/ปี');
      setValue(`tco2e${measureId}`, tco2eDisplay);
      setValue(`tco2e_unit${measureId}`, 'ตัน/ปี');
    });
  }, [measureData, formulaMap, measureTiles, setValue]);

  useEffect(() => {
    measureTiles.forEach((tile) => {
      if (tile.cate_id) {
        fetchFormulasByCategory(tile.cate_id);
      }
    });
  }, [measureTiles, fetchFormulasByCategory]);

  useEffect(() => {
    measureTiles.forEach(tile => {
      tile.measure_lists.forEach(list => {
        fetchMeasureFormula(list.id);
      });
    });
  }, [measureTiles, fetchMeasureFormula]);

  useEffect(() => {
    measureTiles.forEach(tile => {
      let totalSaving = 0;
      let totalTco2e = 0;

      tile.measure_lists.forEach(measure => {
        const measureId = measure.id.toString();

        // รวมผลประหยัด
        const unitArr = measureData[measureId]?.unit || [];
        const saving = unitArr.reduce(
          (sum, v) => sum + (parseFloat(v) || 0),
          0
        );

        totalSaving += saving;

        // รวม tCO2e
        const ef = formulaMap[tile.cate_id]?.ef || 0;
        totalTco2e += (saving * ef) / 1000;
      });

      // set ค่าเข้า form
      setValue(
        `total_saving${tile.id}`,
        totalSaving.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );

      setValue(`total_saving_unit${tile.id}`, 'หน่วย/ปี');

      setValue(
        `total_tco2e${tile.id}`,
        totalTco2e.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );

      setValue(`total_tco2e_unit${tile.id}`, 'ตัน/ปี');
    });
  }, [measureTiles, measureData, formulaMap, setValue]);

  return (
    <>
      {measureTiles.length === 0 && (
        <div className="app-empty-state app-empty-state--top app-empty-state--measure-section">
          <div className="app-empty-state__badge">หมวดนี้ยังไม่มีรายการ</div>
          <h3 className="app-empty-state__title">ยังไม่มีแบบฟอร์มหรือข้อมูลให้กรอกในหมวดนี้</h3>
          <p className="app-empty-state__description">
            ตอนนี้ยังไม่มีรายการมาตรการที่พร้อมให้บันทึกข้อมูลในส่วนนี้
            หากเป็นหมวดอย่างมาตรการอื่น ๆ หรือมาตรการลดการใช้กระดาษ ระบบจะแสดงแบบฟอร์มที่นี่เมื่อมีการตั้งค่ารายการเรียบร้อยแล้ว
          </p>
          <p className="app-empty-state__hint">
            คุณยังไม่ต้องดำเนินการในขั้นตอนนี้ และสามารถกลับมาตรวจสอบภายหลังได้
          </p>
        </div>
      )}
      {measureTiles[currentPage] && (
        <>
          <div className="app-measure-stepper">
            <div className="app-measure-stepper__top">
              <div className="app-measure-stepper__intro">
                <span className="app-measure-stepper__kicker">ขั้นตอนการกรอกข้อมูลมาตรการ</span>
                <div className="app-measure-stepper__title-row">
                  <HiOutlineCollection size={18} className="app-measure-stepper__title-icon" />
                  <strong>{measureTiles[currentPage].name}</strong>
                </div>
                <span className="app-measure-stepper__caption">ขั้นตอน {currentStep} จาก {totalSteps}</span>
              </div>
              <div className="app-measure-stepper__meta">{Math.round(progressValue)}%</div>
            </div>
            <div className="app-measure-stepper__bar" aria-hidden="true">
              <span style={{ width: `${progressValue}%` }}></span>
            </div>
            <div className="app-measure-stepper__steps">
              {measureTiles.map((tile, index) => {
                const stepNumber = index + 1;
                const isActive = index === currentPage;
                const isCompleted = index < currentPage;

                return (
                  <button
                    key={tile.id}
                    type="button"
                    className={`app-measure-stepper__step ${isActive ? 'is-active' : ''} ${isCompleted ? 'is-completed' : ''}`.trim()}
                    onClick={() => {
                      setCurrentPage(index)
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                  >
                    <span className="app-measure-stepper__step-index">{stepNumber}</span>
                    <span className="app-measure-stepper__step-label">{tile.name}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-3">
            {measureTiles[currentPage].measure_lists.map(
              (measure_list, listIndex) => {
                const isChecked = checkedItems[measure_list.id] || false;
                const activityValues = watch(`activity_measure${measure_list.id}`, []);
                const reportValues = watch(`report_measure${measure_list.id}`, []);

                const hasActivitySelected = Array.isArray(activityValues) && activityValues.length > 0;
                const reportSelected = Array.isArray(reportValues) && reportValues.length > 0;

                const isCategory2 = measureTiles[currentPage].cate_id === 2;

                return (
                  <div  key={`${measure_list.id}`} className="px-3 pb-3 pt-0 mb-3 border-bottom">
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      onChange={() => handleCheckboxChange(measure_list.id)}
                      checked={isChecked}
                    />
                    <label htmlFor="" className="form-check-label fw-medium">
                      {listIndex + 1}. {measure_list.name}
                    </label>

                    {isChecked && (
                      <>
                        <input
                          type="hidden"
                          defaultValue={`${measure_list.id}`}
                          {...register(`list_id${measure_list.id}`)}
                        />
                        {/* กิจกรรม/วิธีการดำเนินการบริหารจัดการ */}
                        <div className="mt-4">
                          <div className="fw-semibold mb-2"> กิจกรรม/วิธีการดำเนินการบริหารจัดการ</div>

                          {!isCategory2 ? (
                            activityList.map((activity, i) => {
                              const activityValue = (i + 1).toString();
                              const activityValues = watch(`activity_measure${measure_list.id}`, []);
                              const activityArr = Array.isArray(activityValues)
                                ? activityValues
                                : [activityValues].filter(Boolean);
                              const isChecked =
                                getMeasureResult?.measure?.[`list_id${measure_list.id}`]?.activity_measure?.includes(activityValue)
                                || activityArr.includes(activityValue);

                              return (
                                <div key={i} className="mb-3">

                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      name={`activity_measure${measure_list.id}`}
                                      value={i + 1}
                                      {...register(`activity_measure${measure_list.id}`)}
                                    />
                                    <label className="form-check-label">{activity.label}</label>
                                  </div>
                                  {isChecked && renderActivityFields(measure_list.id, i, measureTiles[currentPage].cate_id, activity.key, measure_list.name)}
                                </div>
                              )
                            })
                          ) : (
                            activityList.map((activity, i) => (
                              <div key={i} className="row mx-1 small">
                                <div className="mb-2 fw-medium fs-5">{activity.label}</div>
                                {renderActivityFields(measure_list.id, i, measureTiles[currentPage].cate_id, activity.key, measure_list.name)}
                              </div>
                            ))
                          )}

                          {!hasActivitySelected && (
                            <div className="alert alert-info mt-3">
                              กรุณาเลือก <strong>กิจกรรม / วิธีการดำเนินการ </strong>
                              ก่อน เพื่อกรอกข้อมูลในส่วนถัดไป
                            </div>
                          )}
                          {hasActivitySelected && (
                            <>
                              <div className="mt-4">
                                <div>
                                  <label htmlFor={`saving_ref${measure_list.id}`} className="fw-semibold mb-2">หลักฐานประกอบ</label>
                                  {renderFileLink('saving_ref', measure_list.id)}
                                  <input
                                    type="file"
                                    name={`saving_ref${measure_list.id}`}
                                    className="form-control form-control-sm mb-2"
                                    id={`saving_ref${measure_list.id}`}
                                    {...register(`saving_ref${measure_list.id}`)}
                                  />
                                </div>
                                <hr />
                              </div>
                              <div className="mt-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <div className="fw-semibold">
                                    เอกสารประกอบ
                                  </div>
                                </div>
                                <div className="bg-light rounded-3 p-3">
                                  <a
                                    href={`${config.urlApi}/path-to-your-excel-template`}
                                    className="d-flex align-items-center text-decoration-none mb-3 text-primary"
                                    download
                                  >
                                    <div
                                      className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                      style={{
                                        width: 36,
                                        height: 36,
                                        background: "rgba(13,110,253,.1)"
                                      }}
                                    >
                                      <IoMdDownload size={20} />
                                    </div>
                                    <div>
                                      <div className="fw-medium">
                                        ไฟล์ Excel อ้างอิงการคำนวณ
                                      </div>
                                      <small className="text-muted">
                                        ใช้เป็น Template สำหรับคำนวณผลลัพธ์มาตรการ
                                      </small>
                                    </div>
                                  </a>
                                  <div className="mb-3">
                                    {renderFileLink('activity_ref', measure_list.id)}
                                  </div>

                                  <div className="mt-2">
                                    <label className="form-label fw-medium" htmlFor={`inputExcel${measure_list.id}`}>
                                      {getMeasureResult?.measure?.[`list_id${measure_list.id}`]?.activity_ref
                                        ? 'เปลี่ยนไฟล์ Excel'
                                        : 'Upload Excel ที่คำนวณเรียบร้อยแล้ว'}
                                    </label>
                                    <input
                                      type="file"
                                      className="form-control form-control-sm"
                                      id={`inputExcel${measure_list.id}`}
                                      {...register(`activity_ref${measure_list.id}`)}
                                      accept=".xls, .xlsx, .pdf, .png, .jpg"
                                    />
                                    <small className="text-muted d-block mt-1">
                                      รองรับไฟล์ .xls, .xlsx, .pdf, .png, .jpg
                                    </small>
                                  </div>
                                </div>
                              </div>
                              <hr />

                              <div className="mt-4">
                                <div className="fw-semibold mb-3">
                                  การรายงาน / ประเมินผล
                                </div>

                                <div className="ps-3">
                                  {renderCheckboxGroup('report_measure', ['รูปแบบเอกสาร', 'รูปแบบ E-report'], measure_list.id)}
                                  {reportSelected && (
                                    <div className="mt-4">
                                      {renderFileLink('report_ref', measure_list.id)}

                                      <div className="mt-3">
                                        <label className="form-label fw-medium" htmlFor={`reportRef${measure_list.id}`}>เอกสารอ้างอิง</label>
                                        <input
                                          type="file"
                                          className="form-control form-control-sm"
                                          id={`reportRef${measure_list.id}`}
                                          {...register(`report_ref${measure_list.id}`)}
                                          accept=".png, .jpg, .pdf, .jpeg"
                                        />
                                        <small className="text-muted d-block mt-1">
                                          รองรับไฟล์ .png, .jpg, .jpeg, .pdf
                                        </small>
                                      </div>
                                    </div>
                                  )}
                                </div>

                              </div>
                              <hr />
                              <div className="mt-4 pt-3 border-top">
                                <div className="fw-semibold mb-2">
                                  ผลลัพธ์
                                </div>
                                <div className="row g-3 mb-3">
                                  {['ผลประหยัด', 'หน่วย'].map((lable, i) => (
                                    <div key={i} className="col-12 col-md-6">
                                      <label className="small text-muted" htmlFor={i === 0 ? `result_saving${measure_list.id}` : `saving_unit${measure_list.id}`}>{lable}</label>
                                      <input
                                        type="text"
                                        name={i === 0 ? `result_saving${measure_list.id}` : `saving_unit${measure_list.id}`}
                                        className="form-control form-control-sm bg-light"
                                        readOnly
                                        id={i === 0 ? `result_saving${measure_list.id}` : `saving_unit${measure_list.id}`}
                                        {...register(i === 0 ? `result_saving${measure_list.id}` : `saving_unit${measure_list.id}`)}
                                      />
                                    </div>
                                  ))}
                                </div>
                                <div className="row g-3">
                                  {['tCO2e', 'หน่วย'].map((lable, i) => (
                                    <div key={i} className="col-12 col-md-6">
                                      <label className="small text-muted" htmlFor={i === 0 ? `tco2e${measure_list.id}` : `tco2e_unit${measure_list.id}`}>{lable}</label>
                                      <input
                                        type="text"
                                        name={i === 0 ? `tco2e${measure_list.id}` : `tco2e_unit${measure_list.id}`}
                                        className="form-control form-control-sm bg-light"
                                        readOnly
                                        id={i === 0 ? `tco2e${measure_list.id}` : `tco2e_unit${measure_list.id}`}
                                        {...register(i === 0 ? `tco2e${measure_list.id}` : `tco2e_unit${measure_list.id}`)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </>
                          )}

                        </div>
                      </>
                    )}
                  </div>
                );
              }
            )}

            <div className="border rounded-3 p-3 mt-4 bg-light">
              <div className="fw-semibold mb-2">สรุปผลรวม</div>
              <div className="row g-3 mb-3">
                {['ผลประหยัด', 'หน่วย'].map((lable, i) => (
                  <div key={i} className="col-12 col-md-6">
                    <label className="small text-muted" htmlFor={i === 0 ? `total_saving${measureTiles[currentPage].id}` : `total_saving_unit${measureTiles[currentPage].id}`}>{lable}</label>
                    <input
                      type="text"
                      name={i === 0 ? `total_saving${measureTiles[currentPage].id}` : `total_saving_unit${measureTiles[currentPage].id}`}
                      className="form-control form-control-sm"
                      readOnly
                      id={i === 0 ? `total_saving${measureTiles[currentPage].id}` : `total_saving_unit${measureTiles[currentPage].id}`}
                      {...register(i === 0 ? `total_saving${measureTiles[currentPage].id}` : `total_saving_unit${measureTiles[currentPage].id}`)}
                    />
                  </div>
                ))}
              </div>
              <div className="row g-3">
                {['tCO2e', 'หน่วย'].map((lable, i) => (
                  <div key={i} className="col-12 col-md-6">
                    <label className="small text-muted" htmlFor={i === 0 ? `total_tco2e${measureTiles[currentPage].id}` : `total_tco2e_unit${measureTiles[currentPage].id}`}>{lable}</label>
                    <input
                      type="text"
                      name={i === 0 ? `total_tco2e${measureTiles[currentPage].id}` : `total_tco2e_unit${measureTiles[currentPage].id}`}
                      className="form-control form-control-sm"
                      readOnly
                      id={i === 0 ? `total_tco2e${measureTiles[currentPage].id}` : `total_tco2e_unit${measureTiles[currentPage].id}`}
                      {...register(i === 0 ? `total_tco2e${measureTiles[currentPage].id}` : `total_tco2e_unit${measureTiles[currentPage].id}`)}
                    />
                  </div>
                ))}
              </div>
              <hr />
            </div>

            <div className="text-end mt-3">
              <button type="submit" className="btn btn-primary">
                บันทึกข้อมูล
              </button>
            </div>

          </form>
        </>
      )}

      {measureTiles.length > 1 && (
        <div className="app-measure-stepper-footer">
          <button
            type="button"
            onClick={handlePreviousPage}
            className="btn btn-outline-secondary"
            disabled={currentPage === 0}
          >
            ขั้นตอนก่อนหน้า
          </button>
          <div className="app-measure-stepper-footer__status">
            <strong>ขั้นตอน {currentStep}/{totalSteps}</strong>
            <span>{measureTiles[currentPage]?.name}</span>
          </div>
          <button
            type="button"
            onClick={handleNextPage}
            className="btn btn-primary"
            disabled={currentPage === measureTiles.length - 1}
          >
            ขั้นตอนถัดไป
          </button>
        </div>
      )}
    </>
  );
}
